/**
 * PortalFormPage — route handler for /forms/:portalSlug/:workflowSlug
 *
 * Loads portal branding and workflow form schema, renders DynamicForm.
 * Uses hooks from the data layer (never accesses repositories directly).
 */
import { useParams } from 'react-router';
import { Alert, Box, Card, Center, Container, Loader, Stack, Title } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { usePortalForm } from '../data/portal/hooks/usePortalForm';
import { useSubmitForm } from '../data/portal/hooks/useSubmitForm';
import { DynamicForm } from '../components/portal/DynamicForm';

export function PortalFormPage() {
  const { portalSlug = '', workflowSlug = '' } = useParams<{
    portalSlug: string;
    workflowSlug: string;
  }>();

  const { portal, workflow, isLoading, error: loadError } = usePortalForm(portalSlug, workflowSlug);
  const { submit, isSubmitting, submission, validationErrors, error: submitError } =
    useSubmitForm(portalSlug, workflowSlug, workflow);

  if (isLoading) {
    return (
      <Center py={120}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (loadError) {
    return (
      <Box py={80}>
        <Container size="sm">
          <Alert icon={<IconAlertCircle size={16} />} title="Form Not Found" color="red" variant="light">
            {loadError.message}
          </Alert>
        </Container>
      </Box>
    );
  }

  if (!portal || !workflow) {
    return (
      <Box py={80}>
        <Container size="sm">
          <Alert icon={<IconAlertCircle size={16} />} title="Not Available" color="yellow" variant="light">
            This form is not currently available.
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <>
      <Box py={60} bg="gray.0">
        <Container size="xl">
          <Stack align="center" gap="sm" maw={600} mx="auto">
            {portal.logoUrl && (
              <img
                src={portal.logoUrl}
                alt={portal.displayName}
                style={{ maxHeight: 60, marginBottom: 8 }}
              />
            )}
            <Title ta="center">{portal.displayName}</Title>
          </Stack>
        </Container>
      </Box>

      <Box py={60}>
        <Container size="sm">
          <Card padding="xl" shadow="sm" radius="lg">
            <DynamicForm
              workflow={workflow}
              onSubmit={async (data) => { await submit(data); }}
              isSubmitting={isSubmitting}
              validationErrors={validationErrors}
              error={submitError}
              submitted={!!submission}
            />
          </Card>
        </Container>
      </Box>
    </>
  );
}
